<template>
    <div>
        <div v-if="showNoCreditsMessage" class="alert p-3 m-b-3 m-t-4 c-muted br br-c--fade c-accent-bg-light">
            <div class="row row-middle row-collapse">
                <span class="i-container i-filled-accent">
                    <svg class="injected-svg icon inject-svg i-size-small m-r-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="24px" height="24px"><path d="M49.306 26.548l-11.24-3.613-3.613-11.241C34.319 11.28 33.935 11 33.5 11s-.819.28-.952.694l-3.613 11.241-11.24 3.613C17.28 26.681 17 27.065 17 27.5s.28.819.694.952l11.24 3.613 3.613 11.241C32.681 43.72 33.065 44 33.5 44s.819-.28.952-.694l3.613-11.241 11.24-3.613C49.72 28.319 50 27.935 50 27.5S49.72 26.681 49.306 26.548zM1.684 13.949l7.776 2.592 2.592 7.776C12.188 24.725 12.569 25 13 25s.813-.275.948-.684l2.592-7.776 7.776-2.592C24.725 13.813 25 13.431 25 13s-.275-.813-.684-.949L16.54 9.459l-2.592-7.776C13.813 1.275 13.431 1 13 1s-.813.275-.948.684L9.46 9.459l-7.776 2.592C1.275 12.188 1 12.569 1 13S1.275 13.813 1.684 13.949zM17.316 39.05l-5.526-1.842-1.842-5.524C9.813 31.276 9.431 31 9 31s-.813.275-.948.684L6.21 37.208.685 39.05c-.408.136-.684.518-.684.949s.275.813.684.949l5.526 1.842 1.841 5.524C8.188 48.721 8.569 48.997 9 48.997s.813-.275.948-.684l1.842-5.524 5.526-1.842C17.725 40.811 18 40.429 18 39.999S17.725 39.186 17.316 39.05z"></path></svg>
                </span>
                <div v-if="planType === 'creator'" class="column">
                    <p class="f-s-2">
                        {{ trans('AI Powered Transcriptions are now available on the Business & Unlimited plans.') }}
                        <a href="/goto/stripe?flow=subscription_update" target="_blank">{{ trans('Upgrade your plan') }}</a>
                    </p>
                </div>
                <div v-else class="column">
                    <p class="f-s-2">
                        {{ trans('You have no AI credits remaining. Please purchase more credits to use this feature.') }}
                        <a href="/account/purchase/credots" v-gleap-new-conversation>{{ trans('Buy Credits') }}</a>
                    </p>
                </div>
            </div>
        </div>
        <div v-else-if="showToolbar">
            <div class="row row-justify row-middle row-collapse m-t-3">
                <div>
                    <p v-if="showStatusMessage" class="c-muted f-s-1">{{ statusMessage }}</p>
                </div>
                <div>
                    <button
                        v-if="showEditButton"
                        v-on:click="openEditModal"
                        type="button"
                        class="btn btn-smaller btn-secondary f-s-2"
                    >
                        {{ this.trans('Edit transcription') }}
                    </button>
                    <button
                        v-if="showGenerateButton"
                        v-on:click="generateTranscription"
                        v-bind:class="{'btn-loading': isProcessing, 'btn-disabled': disableGenerateButton}"
                        v-bind:disabled="disableGenerateButton"
                        type="button"
                        class="btn btn-smaller btn-primary f-s-2"
                    >
                        {{ this.trans('Generate transcription') }}
                        <div v-if="isProcessing" class="loader">
                            <div class="chartdonut chartdonut_animation--rotate-extra-tiny chartdonut--extra-tiny">
                                <svg class="i-stroke-bright" width="18" height="18"><g><circle class="icon" cx="50%" cy="50%" r="6" stroke-width="2" fill="none" stroke-dashoffset="2"></circle></g></svg>
                            </div>
                        </div>
                    </button>
                </div>
            </div>
            <transcription-edit-modal
                ref="editModal"
                v-bind:video-id="videoId"
                v-bind:video-type="videoType"
            ></transcription-edit-modal>
        </div>
    </div>
</template>
<script>
import {
    TRANSCRIPTION_STATUS_CONVERTING_FILE,
    TRANSCRIPTION_STATUS_CONVERTING_FILE_FAILED,
    TRANSCRIPTION_STATUS_PREPARING_SOURCE,
    TRANSCRIPTION_STATUS_SOURCE_FILE_TOO_BIG,
    TRANSCRIPTION_STATUS_TRANSCRIPTION_COMPLETED,
    TRANSCRIPTION_STATUS_TRANSCRIPTION_FAILED,
    TRANSCRIPTION_STATUS_TRANSCRIPTION_IN_PROGRESS,
    TRANSCRIPTION_STATUS_TRANSCRIPTION_PENDING
} from '../../constants';
import { EventBus } from '../../event-bus';
import {
    transcriptionGenerateUrl,
    transcriptionStatusUrl
} from '../../routes';
import TranscriptionEditModal from '../modals/TranscriptionEditModal.vue';

export default {
  components: { TranscriptionEditModal },
    props: {
        videoId: {
            type: String,
            default: null,
        },
        videoType: {
            type: String,
            default: null,
        },
        transcriptionJob: {
            type: Object,
            default: null,
        },
        planType: {
            type: String,
            default: null,
        },
        hasCreditsRemaining: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            status: this.transcriptionJob ? this.transcriptionJob.status : 'not_started',
            statusTimeout: null,
            currentDelay: 10000, // Initial delay of 10 seconds
            editModal: null,
        };
    },
    computed: {
        statusMessage() {
            switch (this.status) {
                case TRANSCRIPTION_STATUS_PREPARING_SOURCE:
                    return this.trans('Preparing source file...');
                case TRANSCRIPTION_STATUS_CONVERTING_FILE:
                    return this.trans('Preparing video file...');
                case TRANSCRIPTION_STATUS_CONVERTING_FILE_FAILED:
                    return this.trans('Preparing video file for transcription failed.');
                case TRANSCRIPTION_STATUS_TRANSCRIPTION_PENDING:
                    return this.trans('Transcription pending...');
                case TRANSCRIPTION_STATUS_SOURCE_FILE_TOO_BIG:
                    return this.trans('Transcription source file too big.');
                case TRANSCRIPTION_STATUS_TRANSCRIPTION_IN_PROGRESS:
                    return this.trans('Transcription in progress...');
                case TRANSCRIPTION_STATUS_TRANSCRIPTION_FAILED:
                    return this.trans('Transcription failed.');
                default:
                    return '';
            }
        },
        isError() {
            return this.status === TRANSCRIPTION_STATUS_CONVERTING_FILE_FAILED
                || this.status === TRANSCRIPTION_STATUS_SOURCE_FILE_TOO_BIG
                || this.status === TRANSCRIPTION_STATUS_TRANSCRIPTION_FAILED;
        },
        isProcessing() {
            return this.isError === false
                && this.status !== 'not_started'
                && this.status !== TRANSCRIPTION_STATUS_TRANSCRIPTION_COMPLETED;
        },
        showStatusMessage() {
            return this.status !== 'not_started' && this.status !== TRANSCRIPTION_STATUS_TRANSCRIPTION_COMPLETED;
        },
        showGenerateButton() {
            return this.status !== TRANSCRIPTION_STATUS_TRANSCRIPTION_COMPLETED;
        },
        disableGenerateButton() {
            return this.status !== 'not_started' && this.status !== TRANSCRIPTION_STATUS_TRANSCRIPTION_COMPLETED;
        },
        showEditButton() {
            return this.status === TRANSCRIPTION_STATUS_TRANSCRIPTION_COMPLETED;
        },
        showToolbar() {
            return this.videoId && this.videoType === 'bunny';
        },
        showNoCreditsMessage() {
            return ! this.hasCreditsRemaining && this.status === 'not_started' && this.videoType === 'bunny';
        },
    },
    methods: {
        openEditModal() {
            this.editModal.show = true;
        },
        checkStatus() {
            if ( ! this.videoId) {
                return;
            }

            // Clear any existing timeout
            if (this.statusTimeout) {
                clearTimeout(this.statusTimeout);
                this.statusTimeout = null;
            }

            // Make the API request to check status
            window.axios.get(transcriptionStatusUrl(this.videoId))
                .then((response) => {
                    if (response.data && response.data.status) {
                        const previousStatus = this.status;
                        this.status = response.data.status;

                        // If still processing, schedule another check
                        if (this.isProcessing) {
                            // Calculate delay based on whether status changed
                            let delay = 10000; // Default: 10 seconds

                            // If status hasn't changed, increase delay (up to 30 seconds max)
                            if (previousStatus === this.status) {
                                // Get current delay from data property or default to 10 seconds
                                delay = this.currentDelay ? Math.min(this.currentDelay * 1.5, 30000) : 10000;
                            } else {
                                // Status changed, reset to default delay
                                delay = 10000;
                            }

                            // Store the calculated delay for next time
                            this.currentDelay = delay;

                            this.statusTimeout = setTimeout(() => {
                                this.checkStatus();
                            }, delay);
                        }
                    }
                })
                .catch((error) => {
                    console.error('Error checking transcription status:', error);
                });
        },
        generateTranscription() {
            this.status = TRANSCRIPTION_STATUS_TRANSCRIPTION_PENDING;
            this.currentDelay = 10000; // Reset delay to 10 seconds

            window.axios.post(transcriptionGenerateUrl(this.videoId))
                .then((response) => {
                    if (response.data && response.data.status) {
                        this.status = response.data.status;

                        this.statusTimeout = setTimeout(() => {
                                this.checkStatus();
                        }, this.currentDelay);
                    }
                })
                .catch((error) => {
                    console.error('Error generating transcription:', error);

                    this.status = TRANSCRIPTION_STATUS_TRANSCRIPTION_FAILED
                });
        },
    },
    mounted() {
        if (this.status !== TRANSCRIPTION_STATUS_TRANSCRIPTION_COMPLETED && ! this.isError) {
            this.statusTimeout = setTimeout(() => {
                this.checkStatus();
            }, this.currentDelay);
        }

        this.editModal = this.$refs.editModal;

        // This is triggered when video is removed
        EventBus.$on('video:video:exists', (status) => {
            if (status === false) {
                this.$destroy();
                this.$el.parentNode.removeChild(this.$el);
            }
        });
    },
    beforeDestroy() {
        // Clean up the timeout when component is destroyed
        if (this.statusTimeout) {
            clearTimeout(this.statusTimeout);
            this.statusTimeout = null;
        }
    }
}
</script>